import React, { useState, useEffect } from 'react';
import { categoryAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2,
  MapPin,
  Plus,
  Edit,
  Trash2,
  Search,
  Download,
  Layers,
  Upload,
  FileSpreadsheet,
} from 'lucide-react';

const AdminCategories = () => {
  const [activeTab, setActiveTab] = useState('developer');
  const [developers, setDevelopers] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [formState, setFormState] = useState('');
  const [importing, setImporting] = useState(false);
  const [showImportResult, setShowImportResult] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [devRes, stateRes, cityRes] = await Promise.all([
        categoryAPI.getCategories({ type: 'developer' }),
        categoryAPI.getCategories({ type: 'state' }),
        categoryAPI.getCategories({ type: 'city' }),
      ]);
      setDevelopers(devRes.data || []);
      const loadedStates = stateRes.data || [];
      const loadedCities = cityRes.data || [];
      setStates(loadedStates);
      setCities(loadedCities);

      // Auto-seed Malaysian locations if no states exist
      if (loadedStates.length === 0) {
        try {
          const seedRes = await categoryAPI.seedLocations();
          toast.success(seedRes.data.message || 'Malaysian locations seeded');
          // Reload after seeding
          const [newStateRes, newCityRes] = await Promise.all([
            categoryAPI.getCategories({ type: 'state' }),
            categoryAPI.getCategories({ type: 'city' }),
          ]);
          setStates(newStateRes.data || []);
          setCities(newCityRes.data || []);
        } catch (seedErr) {
          console.error('Auto-seed failed:', seedErr);
        }
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedLocations = async () => {
    try {
      const res = await categoryAPI.seedLocations();
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Failed to seed locations');
    }
  };

  // Export current list as CSV — doubles as the import template (same column shape).
  const handleExport = () => {
    const isDev = activeTab === 'developer';
    const items = isDev ? developers : cities;
    if (!items.length) {
      toast.error('Nothing to export');
      return;
    }
    const csvEscape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = isDev ? ['Name', 'Created At'] : ['Name', 'Created At'];
    const rows = items.map((it) => [it.name, it.created_at || ''].map(csvEscape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${activeTab}_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`Exported ${items.length} ${activeTab}(s)`);
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const type = activeTab === 'developer' ? 'developer' : 'city';
    setImporting(true);
    try {
      const res = await categoryAPI.bulkImport(type, file);
      setShowImportResult(res.data);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openAdd = () => {
    setEditItem(null);
    setFormName('');
    setFormState(states.length > 0 ? states[0].name : '');
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormName(item.name);
    setFormState(item.state || '');
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editItem) {
        const updateData = { name: formName.trim() };
        if (activeTab === 'location' && formState) updateData.state = formState;
        await categoryAPI.updateCategory(editItem.id, updateData);
        toast.success('Updated successfully');
      } else {
        const payload = { name: formName.trim() };
        if (activeTab === 'developer') {
          payload.type = 'developer';
        } else {
          payload.type = 'city';
          payload.state = formState;
        }
        await categoryAPI.createCategory(payload);
        toast.success('Created successfully');
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await categoryAPI.deleteCategory(id);
      toast.success('Deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Filter logic
  const filteredDevelopers = developers.filter((d) =>
    search ? d.name.toLowerCase().includes(search.toLowerCase()) : true
  );

  const filteredCities = cities.filter((c) => {
    const matchSearch = search ? c.name.toLowerCase().includes(search.toLowerCase()) : true;
    const matchState = selectedState !== 'all' ? c.state === selectedState : true;
    return matchSearch && matchState;
  });

  const tabs = [
    { id: 'developer', label: 'Developers', icon: Building2, count: developers.length },
    { id: 'location', label: 'Locations', icon: MapPin, count: `${states.length} states, ${cities.length} cities` },
  ];

  return (
    <div className="space-y-6" data-testid="admin-categories">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-500 mt-1">Manage master data for developers and locations</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSearch(''); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              <Badge variant="outline" className="ml-1 text-xs">{tab.count}</Badge>
            </button>
          );
        })}
      </div>

      {/* Hidden file input for bulk import */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".xlsx,.xls,.csv"
        onChange={handleBulkImport}
      />

      {/* Import Result Dialog */}
      <Dialog open={!!showImportResult} onOpenChange={() => setShowImportResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Result</DialogTitle>
          </DialogHeader>
          {showImportResult && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">{showImportResult.imported}</div>
                  <div className="text-xs text-green-600">Imported</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <div className="text-2xl font-bold text-amber-700">{showImportResult.skipped}</div>
                  <div className="text-xs text-amber-600">Skipped</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-700">{showImportResult.total_rows}</div>
                  <div className="text-xs text-gray-600">Total Rows</div>
                </div>
              </div>
              {showImportResult.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
                  <ul className="text-xs text-red-600 space-y-0.5 max-h-40 overflow-y-auto">
                    {showImportResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <Button className="w-full" onClick={() => setShowImportResult(null)}>Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Developer Tab */}
      {activeTab === 'developer' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search developers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-developer"
              />
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={openAdd} data-testid="add-developer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Developer
            </Button>
            <Button variant="outline" onClick={handleExport} data-testid="export-developers-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button 
              variant="outline"
              onClick={() => fileInputRef.current?.click()} 
              disabled={importing}
              data-testid="import-developers-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold w-12">#</TableHead>
                  <TableHead className="font-semibold">Developer Name</TableHead>
                  <TableHead className="font-semibold">Created</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-12"><div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto" /></TableCell></TableRow>
                ) : filteredDevelopers.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-12 text-gray-500">No developers found. Add your first developer.</TableCell></TableRow>
                ) : (
                  filteredDevelopers.map((dev, idx) => (
                    <TableRow key={dev.id} className="hover:bg-gray-50" data-testid={`developer-row-${dev.id}`}>
                      <TableCell className="text-gray-400 font-mono text-sm">{idx + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-gray-900">{dev.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {dev.created_at ? new Date(dev.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(dev)} data-testid={`edit-dev-${dev.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(dev.id, dev.name)} data-testid={`delete-dev-${dev.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Location Tab */}
      {activeTab === 'location' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search cities/areas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-location"
              />
            </div>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-48" data-testid="state-filter">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {states.map((s) => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={openAdd} data-testid="add-city-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add City / Area
            </Button>
            <Button variant="outline" onClick={handleExport} data-testid="export-cities-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button 
              variant="outline"
              onClick={() => fileInputRef.current?.click()} 
              disabled={importing}
              data-testid="import-cities-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importing ? 'Importing...' : 'Import'}
            </Button>
            {states.length === 0 && (
              <Button variant="outline" onClick={handleSeedLocations} data-testid="seed-locations-btn">
                <Download className="w-4 h-4 mr-2" />
                Seed Malaysian Locations
              </Button>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold w-12">#</TableHead>
                  <TableHead className="font-semibold">City / Area</TableHead>
                  <TableHead className="font-semibold">State</TableHead>
                  <TableHead className="font-semibold">Created</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12"><div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto" /></TableCell></TableRow>
                ) : filteredCities.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-gray-500">
                    {states.length === 0 ? 'No locations found. Click "Seed Malaysian Locations" to populate.' : 'No cities match your search.'}
                  </TableCell></TableRow>
                ) : (
                  filteredCities.map((city, idx) => (
                    <TableRow key={city.id} className="hover:bg-gray-50" data-testid={`city-row-${city.id}`}>
                      <TableCell className="text-gray-400 font-mono text-sm">{idx + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-red-400" />
                          <span className="font-medium text-gray-900">{city.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{city.state}</Badge>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {city.created_at ? new Date(city.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(city)} data-testid={`edit-city-${city.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(city.id, city.name)} data-testid={`delete-city-${city.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editItem ? 'Edit' : 'Add'} {activeTab === 'developer' ? 'Developer' : 'City / Area'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-gray-700">
                {activeTab === 'developer' ? 'Developer Name' : 'City / Area Name'} *
              </label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={activeTab === 'developer' ? 'e.g., SP Setia Berhad' : 'e.g., Mont Kiara'}
                className="mt-1"
                data-testid="category-name-input"
              />
            </div>
            {activeTab === 'location' && (
              <div>
                <label className="text-sm font-medium text-gray-700">State *</label>
                <Select value={formState} onValueChange={setFormState}>
                  <SelectTrigger className="mt-1" data-testid="category-state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((s) => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} data-testid="save-category-btn">
                {editItem ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCategories;
